import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Tooltip,
  Typography,
  List,
  ListItem,
  ListItemIcon,
} from "@mui/material";
import type { ChipProps } from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import Collapse from "@mui/material/Collapse";
import Box from "@mui/material/Box";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import VerifyThroughArgyle from "./ProofOfEmploymentIncome/VerifyThroughArgyle";
import VerifyThroughPlaid from "./ProofOfEmploymentIncome/VerifyThroughPlaid";
import StopSignIcon from "./components/StopSignIcon";
import { scrollToAnchor } from "./ProofOfEmploymentIncome/scrollToAnchor";
import AlertDialog from "../common/AlertDialog";
import ShowValidationMessages from "./components/ShowValidationMessages";
import ShowValidationMessagesApplicant from "./components/ShowValidationMessagesApplicant";
import { toIncomeSourceTitleCase } from "../../utils/incomeSourceDisplay";

const BANK_LINKING_VALIDATION_SOURCE_ID = 0;

type Props = {
  validationCheckForPlaidBankIncome?: {
    id?: string;
    avgMonthlyIncome?: number;
    banks?: any[];
  };
  validationCheckForArgyleBankIncome?: {
    id?: string;
    avgMonthlyIncome?: number;
    banks?: any[];
    overallStatus?: string;
    failure?: {
      code: string;
      message: string;
      nextStep: string;
    } | null;
    validation_messages?: any[];
  };
  className?: string;
  trn_id?: string;
  plaidEnabled?: boolean;
  plaidEnablementLoading?: boolean;
  plaidEnablementError?: boolean;
  plaidValidationLoading?: boolean;
  plaidValidationError?: boolean;
  enabled?: boolean;
  enablementLoading?: boolean;
  enablementError?: boolean;
  validationLoading?: boolean;
  validationError?: boolean;
  showPendingProcessing?: boolean;
  onRetry?: () => void;
  onBankRemoved?: () => void;
  role?: string | null;
  required?: boolean;
};

type ArgyleBankOverallStatus =
  | "none"
  | "pending"
  | "linked"
  | "linked_no_data"
  | "failed";

type CategoryMeta = {
  label: string;
  color: ChipProps["color"];
  helper: string;
  tooltip: string;
};

const getCategoryMeta = (src: any): CategoryMeta => {
  const cat = (src?.holder_category ?? "unrecognized").toLowerCase();
  switch (cat) {
    case "personal":
      return {
        label: "Account Type: Personal",
        color: "info",
        helper: "Included in average monthly net income",
        tooltip: "Personal accounts are included in income calculations",
      };
    case "business":
      return {
        label: "Account Type: Business",
        color: "default",
        helper: "Excluded from average monthly net income",
        tooltip: "Business accounts are excluded from income calculations",
      };
    default:
      return {
        label: "Account Type: Unknown",
        color: "info",
        helper: "Included in average monthly net income",
        tooltip: "Unknown accounts are included in income calculations",
      };
  }
};

const ArgyleBankIncomeSection: React.FC<Props> = ({
  validationCheckForPlaidBankIncome,
  validationCheckForArgyleBankIncome,
  className,
  trn_id,
  plaidEnabled = false,
  plaidEnablementLoading = false,
  plaidEnablementError = false,
  plaidValidationLoading = false,
  plaidValidationError = false,
  enabled = false,
  enablementLoading = false,
  enablementError = false,
  validationLoading = false,
  validationError = false,
  showPendingProcessing = false,
  onRetry,
  onBankRemoved,
  role,
  required = false,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [txnsVisible, setTxnsVisible] = useState<Record<string, boolean>>({});

  const incomeSources = useMemo(() => {
    const flatten = (banks: any[] = [], provider: "plaid" | "argyle") =>
      banks
      .flatMap((bank: any, bi: number) => {
        const rows = (bank?.bank_income_accounts || []).flatMap((acct: any, ai: number) =>
          (acct?.bank_income_sources || []).map((src: any, si: number) => ({
            bank_name: bank?.bank_name,
            account_name: acct?.account_name || acct?.name,
            provider,
            src,
            key: `${provider}-${bi}-${ai}-${si}`,
          }))
        );
        if (rows.length > 0 || bank?.no_income_accounts_found !== true) return rows;
        return [{
          bank_name: bank?.bank_name,
          account_name: null,
          provider,
          src: {
            no_income_accounts_found: true,
            income_description: "No accounts with income found",
            transactions: [],
          },
          key: `${provider}-${bi}-no-income`,
        }];
      });
    return [
      ...flatten(validationCheckForPlaidBankIncome?.banks ?? [], "plaid"),
      ...flatten(validationCheckForArgyleBankIncome?.banks ?? [], "argyle"),
    ]
      .sort((a, b) => {
        const aExcluded =
          a?.src?.holder_category === "business" ||
          a?.src?.no_current_deposits === true ||
          a?.src?.used_no_current_data_fallback === true
            ? 1
            : 0;
        const bExcluded =
          b?.src?.holder_category === "business" ||
          b?.src?.no_current_deposits === true ||
          b?.src?.used_no_current_data_fallback === true
            ? 1
            : 0;
        if (aExcluded !== bExcluded) return aExcluded - bExcluded;
        return (a?.src?.income_description || "").localeCompare(
          b?.src?.income_description || "",
          undefined,
          { sensitivity: "base" }
        );
      });
  }, [
    validationCheckForArgyleBankIncome?.banks,
    validationCheckForPlaidBankIncome?.banks,
  ]);

  const plaidAvgMonthlyIncome =
    Number(validationCheckForPlaidBankIncome?.avgMonthlyIncome) || 0;
  const argyleAvgMonthlyIncome =
    Number(validationCheckForArgyleBankIncome?.avgMonthlyIncome) || 0;
  const avgMonthlyIncome = plaidAvgMonthlyIncome + argyleAvgMonthlyIncome;
  const hasPlaidData =
    plaidAvgMonthlyIncome > 0 ||
    (validationCheckForPlaidBankIncome?.banks?.length ?? 0) > 0;
  const hasArgyleData =
    argyleAvgMonthlyIncome > 0 ||
    (validationCheckForArgyleBankIncome?.banks?.length ?? 0) > 0;
  const rawOverallStatus = String(
    validationCheckForArgyleBankIncome?.overallStatus || "none"
  ).toLowerCase();
  const overallStatus: ArgyleBankOverallStatus = (
    ["none", "pending", "linked", "linked_no_data", "failed"] as const
  ).includes(rawOverallStatus as ArgyleBankOverallStatus)
    ? (rawOverallStatus as ArgyleBankOverallStatus)
    : "none";
  const rawBankLinkingValidationMessages =
    validationCheckForArgyleBankIncome?.validation_messages;
  const bankLinkingValidationMessages = Array.isArray(rawBankLinkingValidationMessages)
    ? rawBankLinkingValidationMessages
    : [];
  const sectionVisible =
    plaidEnabled ||
    plaidEnablementLoading ||
    plaidEnablementError ||
    plaidValidationLoading ||
    plaidValidationError ||
    hasPlaidData ||
    enabled ||
    enablementLoading ||
    enablementError ||
    validationLoading ||
    validationError ||
    hasArgyleData ||
    overallStatus !== "none" ||
    bankLinkingValidationMessages.length > 0;

  const trnId =
    trn_id ||
    validationCheckForArgyleBankIncome?.id ||
    validationCheckForPlaidBankIncome?.id ||
    "0";
  const employmentIncomeAnchorId = `employment-income-anchor-${trnId}`;
  const bankFailure = validationCheckForArgyleBankIncome?.failure;
  const isClientExit =
    overallStatus === "failed" &&
    String(bankFailure?.code || "").toLowerCase() === "client_exit";
  const showSupportValidationMessages = role !== "applicant";
  const subscriberValidationMessages = bankLinkingValidationMessages
    .map((item) => item?.admin)
    .filter((item) => item?.message && (role === "Docuverus Super Admin" || !item?.isDeleted));
  const applicantValidationMessages = bankLinkingValidationMessages
    .map((item) => item?.applicant)
    .filter((item) => item?.message && (role === "Docuverus Super Admin" || !item?.isDeleted));
  const showLoading =
    (plaidEnablementLoading && !plaidEnablementError) ||
    (plaidEnabled && plaidValidationLoading && !plaidValidationError && !hasPlaidData) ||
    (enablementLoading && !enablementError) ||
    (enabled && validationLoading && !validationError && !hasArgyleData);
  const showPlaidError = plaidEnablementError || (plaidEnabled && plaidValidationError);
  const showArgyleError = enablementError || (enabled && validationError);

  const hasPendingBankStatus = useMemo(() => {
    const banks = validationCheckForArgyleBankIncome?.banks ?? [];
    return banks.some((bank: any) => {
      const bankStatus = String(bank?.status || "").toLowerCase();
      if (bankStatus === "pending") return true;
      const accounts = Array.isArray(bank?.bank_income_accounts)
        ? bank.bank_income_accounts
        : [];
      return accounts.some((acct: any) => {
        const sources = Array.isArray(acct?.bank_income_sources)
          ? acct.bank_income_sources
          : [];
        return sources.some((src: any) => {
          const srcStatus = String(src?.status || "").toLowerCase();
          return srcStatus === "pending" || srcStatus === "processing";
        });
      });
    });
  }, [validationCheckForArgyleBankIncome?.banks]);
  const shouldShowPendingMessage =
    showPendingProcessing || overallStatus === "pending" || hasPendingBankStatus;
  const showNoDataSummary =
    !shouldShowPendingMessage &&
    incomeSources.length === 0 &&
    (overallStatus === "none" ||
      (overallStatus === "linked" && avgMonthlyIncome <= 0));
  const showArgyleButton = enabled;
  const showPlaidButton = !enabled && plaidEnabled;
  const showDisabledLinkButton =
    !showArgyleButton &&
    !showPlaidButton &&
    !enablementLoading &&
    !plaidEnablementLoading &&
    !enablementError &&
    !plaidEnablementError;

  const toggleTxns = (key: string) =>
    setTxnsVisible((prev) => ({ ...prev, [key]: !prev[key] }));

  if (!sectionVisible) return null;

  return (
    <div className={`info-toggle ${className || ""}`}>
      <div className="toggle-heads">
        <div className="row align-items-center justify-content-between">
          <div className="col">
            <div className="d-flex align-items-center">
              <b className="ms-3 me-2">Income from Bank Linking</b>
              {required && <span className="new-star">*</span>}
              <AlertDialog
                message={
                  <>
                    <strong> Why Bank Linking? </strong>
                    <br />
                    Bank linking provides a fast, secure option to share your income related bank deposits. It handles the entire connection so Docuverus never sees nor stores your bank username or password. Bank linking simply shares a read-only summary of your recent payroll deposits so your income can be confirmed.
                    <br /> <br />
                    While bank linking can speed things up, it is completely optional. The most reliable way to qualify remains uploading original earnings documents (such as paystubs or payroll reports) to Docuverus, which you have downloaded directly from your payroll provider.
                  </>
                }
              />
            </div>
          </div>
          <div className="col text-end m-1">
            <IconButton
              size="small"
              className="print-hide"
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? <RemoveIcon /> : <AddIcon />}
            </IconButton>
          </div>
        </div>

        <div className="row align-items-center justify-content-between">
          <div className="col ms-3 mb-2 me-2">
            {shouldShowPendingMessage && (
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <CircularProgress size={16} thickness={5} />
                  <p style={{ margin: 0 }}>Bank income is still processing.</p>
                </div>
                <p className="mb-0" style={{ fontSize: 13 }}>
                  Please be patient while your data is being processed. Some information may not be available right away and can take up to 24 hours to appear.
                </p>
              </div>
            )}
            {overallStatus === "failed" && !shouldShowPendingMessage && (
              isClientExit ? (
                <p style={{ fontSize: 13 }}>Bank linking was not completed.</p>
              ) : (
                <Alert severity="error" sx={{ mb: 1 }}>
                  <strong>{bankFailure?.message || "Bank linking failed or was not completed."}</strong>
                  <div>
                    {bankFailure?.nextStep ||
                      "Try bank linking again or use another verification method."}
                  </div>
                </Alert>
              )
            )}
            {overallStatus === "linked_no_data" && (
              <p style={{ fontSize: 13 }}>Bank linking completed but no usable income data was returned.</p>
            )}
            <div style={{ marginTop: shouldShowPendingMessage ? 8 : 0 }}>
              {avgMonthlyIncome > 0 && (
                <p>Avg Monthly Net Income: ${avgMonthlyIncome.toFixed(2)}</p>
              )}
              {showNoDataSummary && (
                <p style={{ fontSize: 13 }}>No bank income data available.</p>
              )}
            </div>
          </div>
          <div
            className="col-12 col-md-auto ms-3 mt-2 order-1 order-md-2 d-flex justify-content-end flex-shrink-1"
            style={{ marginBottom: "5px", minWidth: 0 }}
          >
            <div
              className="d-inline-flex flex-column align-items-start pe-2"
              style={{ maxWidth: "100%" }}
            >
              {showArgyleButton && (
                <VerifyThroughArgyle
                  trn_id={trnId}
                  linkType="bank"
                  triggerLabel={
                    overallStatus === "failed" && !isClientExit
                      ? "Try bank linking again"
                      : "Add bank income"
                  }
                  includeRelinkWarning={true}
                  triggerContainerClassName="d-flex flex-column align-items-start"
                  triggerContainerStyle={{ width: "auto", maxWidth: "100%" }}
                  triggerRowClassName="d-flex justify-content-start"
                  triggerRowStyle={{ width: "auto", marginTop: 0 }}
                  onBankRemoved={onBankRemoved}
                />
              )}
              {showPlaidButton && (
                <VerifyThroughPlaid
                  trn_id={trnId}
                  linkType="bank"
                  triggerLabel="Add bank income"
                  includeRelinkWarning={true}
                  triggerContainerClassName="d-flex flex-column align-items-start"
                  triggerContainerStyle={{ width: "auto", maxWidth: "100%" }}
                  triggerRowClassName="d-flex justify-content-start"
                  triggerRowStyle={{ width: "auto", marginTop: 0 }}
                />
              )}
              {showDisabledLinkButton && (
                <Tooltip title="You cannot link more bank income for this request.">
                  <span>
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-none"
                      disabled
                      style={{ fontSize: "15px", color: "#6c757d", pointerEvents: "none" }}
                      aria-label="Add bank income"
                    >
                      Add bank income
                    </button>
                  </span>
                </Tooltip>
              )}
              <div
                className="mt-2 px-2 py-1 rounded d-flex align-items-start"
                style={{
                  backgroundColor: "#fff3cd",
                  color: "#664d03",
                  fontSize: 13,
                  textAlign: "left",
                  width: "min(100%, 320px)",
                  gap: "6px",
                }}
              >
                <StopSignIcon style={{ marginTop: "1px" }} />
                <span>
                  Only add bank income if you can&apos;t{" "}
                  <a
                    href={`#${employmentIncomeAnchorId}`}
                    onClick={(event) => {
                      event.preventDefault();
                      scrollToAnchor(employmentIncomeAnchorId, 3000);
                    }}
                  >
                    upload paystubs
                  </a>
                  .
                </span>
              </div>
            </div>
          </div>
        </div>

        {showPlaidError && (
          <div className="px-3 pb-2">
            <Alert
              severity="warning"
              action={
                onRetry ? (
                  <Button color="inherit" size="small" onClick={onRetry}>
                    Retry
                  </Button>
                ) : null
              }
            >
              Unable to load Plaid bank income data right now.
            </Alert>
          </div>
        )}
        {showArgyleError && (
          <div className="px-3 pb-2">
            <Alert
              severity="warning"
              action={
                onRetry ? (
                  <Button color="inherit" size="small" onClick={onRetry}>
                    Retry
                  </Button>
                ) : null
              }
            >
              Unable to load Argyle bank income data right now.
            </Alert>
          </div>
        )}

        {showLoading && (
          <div className="px-3 pb-2">
            <p className="mb-0">Loading bank linking details...</p>
          </div>
        )}

        <div className={`card-body cust-card-appl${expanded ? "" : " d-none"}`}>
        <div className="card card-body w-100 px-0" style={{ paddingTop: 0 }}>
          <div className="px-3 py-2">
            {role === "applicant" && (
              <ShowValidationMessagesApplicant
                validation_message={applicantValidationMessages}
              />
            )}

            {showSupportValidationMessages && (
              <>
                <ShowValidationMessages
                  role={role}
                  validation_message={subscriberValidationMessages}
                  trn_id={trnId}
                  source_id={BANK_LINKING_VALIDATION_SOURCE_ID}
                  section="Bank Linking"
                  invalidateQueries="argyleBankIncomeValidationCheck"
                  tag="updateValidationMessages_bank_linking"
                  document_id={6}
                />
                <ShowValidationMessages
                  role={role}
                  validation_message={applicantValidationMessages}
                  isApplicantTitle={true}
                  trn_id={trnId}
                  source_id={BANK_LINKING_VALIDATION_SOURCE_ID}
                  section="Bank Linking"
                  invalidateQueries="argyleBankIncomeValidationCheck"
                  tag="updateValidationMessages_bank_linking"
                  document_id={6}
                />
              </>
            )}

            {!showLoading &&
              incomeSources.length === 0 && (
              <p className="mb-0" style={{ fontSize: 13 }}>No linked bank income sources found.</p>
            )}

            {!showLoading &&
              incomeSources.map(({ bank_name, account_name, provider, src, key }: any) => {
                const noIncomeAccountsFound = src?.no_income_accounts_found === true;
                const category = getCategoryMeta(src);
                const txns = Array.isArray(src?.transactions)
                  ? src.transactions
                  : Array.isArray(src?.txns)
                    ? src.txns
                    : [];
                const noCurrentDeposits = src?.no_current_deposits === true;
                const avgMonthlyNet = Number(
                  src?.calculated_avg_monthly_income_net ?? src?.avg_monthly_income_net
                );
                const hasAvgMonthlyNet = Number.isFinite(avgMonthlyNet);
                const providerFrequency = src?.pay_frequency || src?.income_frequency || "-";
                const calculatedFrequency = src?.calculated_pay_frequency || "-";
                const displayFrequency =
                  provider === "argyle" ? calculatedFrequency : providerFrequency;
                const inclusionText =
                  noIncomeAccountsFound
                    ? "Connected bank found, but no accounts with income were identified"
                    : category.helper;
                return (
                  <Card key={key} sx={{ mb: 1, mx: 0 }}>
                    <CardHeader
                      title={
                        provider === "argyle"
                          ? toIncomeSourceTitleCase(src?.income_description) || "Income Source"
                          : src?.income_description || "Income Source"
                      }
                      subheader={
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          {inclusionText}
                        </Typography>
                      }
                      titleTypographyProps={{
                        variant: "subtitle1",
                        component: "div",
                      }}
                      action={
                        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                          {provider === "plaid" && !noIncomeAccountsFound && (
                            <Chip
                              size="small"
                              label="Plaid"
                              variant="outlined"
                              sx={{ borderWidth: 2, fontWeight: 600, borderColor: "#1d4ed8", color: "#1d4ed8" }}
                            />
                          )}
                          {!noIncomeAccountsFound && (
                            <Tooltip title={category.tooltip}>
                              <Chip
                                size="small"
                                label={category.label}
                                color={category.color}
                                variant="outlined"
                                sx={{
                                  borderWidth: 2,
                                  fontWeight: 600,
                                  ...(category.color === "default" && {
                                    color: "#9e9e9e",
                                    borderColor: "currentColor",
                                  }),
                                }}
                              />
                            </Tooltip>
                          )}
                        </div>
                      }
                      sx={{
                        bgcolor: "grey.100",
                        py: 0.5,
                        "& .MuiCardHeader-action": {
                          alignSelf: "center",
                          mt: 0,
                        },
                      }}
                    />

                    <CardContent sx={{ pt: 1.25 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                        <strong>Source:</strong> {bank_name || "Unknown Bank"}
                      </Typography>
                      {!noIncomeAccountsFound && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Account:</strong> {account_name || "-"}
                        </Typography>
                      )}
                      {!noIncomeAccountsFound && hasAvgMonthlyNet && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Avg Monthly Net Income:</strong> ${avgMonthlyNet.toFixed(2)}
                        </Typography>
                      )}
                      {!noIncomeAccountsFound && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Frequency:</strong> {displayFrequency}
                        </Typography>
                      )}

                      {txns.length > 0 && (
                        <>
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            onClick={() => toggleTxns(key)}
                          >
                            {txnsVisible[key]
                              ? "- Hide Deposits"
                              : "+ Show Deposits"}
                          </button>
                          <Collapse in={!!txnsVisible[key]} unmountOnExit>
                            <Box sx={{ mt: 1 }}>
                              <List dense disablePadding>
                                {txns.map((t: any, idx: number) => (
                                  <ListItem key={idx} disableGutters sx={{ py: 0.25 }}>
                                    <ListItemIcon sx={{ minWidth: 20 }}>
                                      <FiberManualRecordIcon sx={{ fontSize: 8 }} />
                                    </ListItemIcon>
                                    <Typography variant="body2">
                                      {t?.date || "-"}: ${Number(t?.amount || 0).toFixed(2)}
                                    </Typography>
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          </Collapse>
                        </>
                      )}
                      {!noIncomeAccountsFound && noCurrentDeposits && txns.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
                          No recent deposits found.
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                );
              })}

          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default ArgyleBankIncomeSection;
